import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle as CheckCircleIcon } from 'lucide-react';
import { cn } from '../../../../utils/cn';
import AppIcon, { type IconName } from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { StatusBadge } from '../../../../components/ui/StatusBadge';
import { Alert } from '../../../../components/ui/Alert';
import type { ReflectionPrompt } from '../types';

interface ReflectionPromptCardProps {
  prompt: ReflectionPrompt;
  response: string;
  isSubmitted: boolean;
  onSave: (promptId: string, response: string) => Promise<void>;
  onSubmit: (promptId: string, response: string) => Promise<void>;
  index: number;
  totalPrompts: number;
  canSave: boolean;
  canSubmit: boolean;
  deadlinePassed: boolean;
}

const iconGradients: Record<string, string> = {
  Trophy: 'from-amber-500 to-amber-500/60',
  Heart: 'from-primary to-primary/60',
  TrendingUp: 'from-emerald-500 to-emerald-500/60',
  Shield: 'from-violet-500 to-violet-500/60',
};

const ReflectionPromptCard: React.FC<ReflectionPromptCardProps> = ({
  prompt,
  response,
  isSubmitted,
  onSave,
  onSubmit,
  index,
  totalPrompts,
  canSave,
  canSubmit,
  deadlinePassed,
}) => {
  const { t } = useTranslation();
  const [currentResponse, setCurrentResponse] = useState<string>(response || '');
  const [isEditing, setIsEditing] = useState<boolean>(!isSubmitted);
  const [showTips, setShowTips] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const maxChars = 1000;
  const minChars = 50;
  const charCount = currentResponse.length;

  const handleSave = async () => {
    if (!currentResponse.trim() || !canSave) return;
    setIsSaving(true);
    try {
      await onSave(prompt.id, currentResponse);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!currentResponse.trim() || !canSubmit) return;
    setIsSubmitting(true);
    try {
      await onSubmit(prompt.id, currentResponse);
      setIsEditing(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = () => {
    if (deadlinePassed && isSubmitted) return;
    setIsEditing(true);
  };

  const canEdit = !deadlinePassed || !isSubmitted;
  const saveDisabled = !currentResponse.trim() || !canSave || isSaving;
  const submitDisabled = !currentResponse.trim() || !canSubmit || isSubmitted || isSubmitting;
  const editDisabled = !canEdit || isSubmitting || isSaving;

  return (
    <div
      className={cn(
        'border rounded-xl mb-6 transition-all duration-300 bg-card',
        isSubmitted ? 'border-2 border-emerald-500' : 'border-border',
        deadlinePassed && !isSubmitted && 'opacity-80'
      )}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex gap-4 items-start mb-4">
          <div
            className={cn(
              'w-12 h-12 rounded-lg flex items-center justify-center shrink-0 bg-gradient-to-br',
              iconGradients[prompt.icon] || 'from-primary to-primary/60'
            )}
          >
            <AppIcon name={(prompt.icon as IconName) || 'Trophy'} size={24} className="text-white" />
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <h3 className="text-lg font-semibold text-foreground">{prompt.title}</h3>
              {isSubmitted && (
                <StatusBadge variant="success" icon={CheckCircleIcon}>
                  {t('performance.reflections.submitted')}
                </StatusBadge>
              )}
              {deadlinePassed && !isSubmitted && (
                <StatusBadge variant="warning">
                  {t('performance.reflections.deadlinePassed')}
                </StatusBadge>
              )}
            </div>

            <span className="inline-block text-xs text-muted-foreground bg-muted border border-border rounded-full px-2 py-0.5 mb-3">
              {prompt.category}
            </span>

            <p className="text-sm font-medium text-foreground mb-1">{prompt.question}</p>
            <p className="text-sm text-muted-foreground">{prompt.subtext}</p>
          </div>
        </div>

        {/* Deadline warning */}
        {deadlinePassed && !isSubmitted && (
          <Alert variant="warning" className="mb-4">
            {t('performance.reflections.deadlinePassedMessage')}
          </Alert>
        )}

        {/* Response area */}
        {isEditing ? (
          <>
            <textarea
              rows={6}
              value={currentResponse}
              onChange={(e) => setCurrentResponse(e.target.value)}
              placeholder={prompt.placeholder}
              maxLength={maxChars}
              disabled={deadlinePassed && isSubmitted}
              className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-y mb-4"
            />

            <div className="flex justify-between items-center mb-4">
              <span
                className={cn(
                  'text-xs font-medium',
                  charCount < minChars
                    ? 'text-muted-foreground'
                    : charCount > maxChars * 0.9
                      ? 'text-amber-500'
                      : 'text-emerald-500'
                )}
              >
                {t('performance.reflections.charCount', {
                  current: charCount,
                  max: maxChars,
                  min: minChars,
                })}
              </span>
            </div>

            <div className="flex gap-2 flex-wrap mb-4">
              <Button
                onClick={handleSubmit}
                disabled={submitDisabled}
                loading={isSubmitting}
                iconName="Send"
                title={
                  !canSubmit
                    ? t('performance.reflections.cannotSubmitAfterDeadline')
                    : isSubmitted
                      ? t('performance.reflections.alreadySubmitted')
                      : ''
                }
              >
                {isSubmitting
                  ? t('performance.reflections.submitting')
                  : t('performance.reflections.submitResponse')}
              </Button>

              <Button
                variant="outline"
                onClick={handleSave}
                disabled={saveDisabled}
                loading={isSaving}
                iconName="Save"
                title={!canSave ? t('performance.reflections.cannotSaveAfterDeadline') : ''}
              >
                {isSaving
                  ? t('performance.reflections.saving')
                  : t('performance.reflections.saveDraft')}
              </Button>

              <Button
                variant="ghost"
                onClick={() => setShowTips(!showTips)}
                iconName={showTips ? 'ChevronUp' : 'ChevronDown'}
                iconPosition="right"
              >
                {t('performance.reflections.tips')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="p-4 bg-muted rounded-lg border border-border mb-4 min-h-[120px]">
              <p className={cn('text-sm', currentResponse ? 'text-foreground' : 'text-muted-foreground')}>
                {currentResponse || t('performance.reflections.noResponseYet', "No response yet. Click 'Edit Response' to get started.")}
              </p>
            </div>

            <div className="flex gap-2 flex-wrap">
              {!isSubmitted && (
                <Button
                  variant="outline"
                  onClick={handleEdit}
                  disabled={editDisabled}
                  iconName="Pencil"
                  title={editDisabled ? (deadlinePassed ? t('performance.reflections.cannotSaveAfterDeadline') : '') : ''}
                >
                  {t('performance.reflections.editResponse')}
                </Button>
              )}

              <Button
                variant="ghost"
                onClick={() => setShowTips(!showTips)}
                iconName={showTips ? 'ChevronUp' : 'ChevronDown'}
                iconPosition="right"
              >
                {t('performance.reflections.tips')}
              </Button>
            </div>
          </>
        )}

        {/* Tips section */}
        <div
          className={cn(
            'overflow-hidden transition-all duration-300',
            showTips ? 'max-h-96 mt-4' : 'max-h-0'
          )}
        >
          <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-2 mb-2">
              <AppIcon name="Lightbulb" size={16} className="text-amber-500" />
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                {t('performance.reflections.reflectionTips')}
              </p>
            </div>
            <ul className="list-disc ps-6 space-y-1">
              {prompt.tips.map((tip, tipIndex) => (
                <li key={tipIndex} className="text-sm text-amber-900 dark:text-amber-100">
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Status */}
        <div className="flex justify-between items-center mt-4">
          <span className="text-xs text-muted-foreground">
            {t('performance.reflections.promptOf', { current: index + 1, total: totalPrompts })}
          </span>
          {deadlinePassed && (
            <span className="text-xs text-amber-500 italic">
              {t('performance.reflections.nextMonthOpens')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReflectionPromptCard;
